import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import PrimaryButton from '../../../components/styled/PrimaryButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatAmount } from '../../../utils/formatters';
import { formatDateToDDMMYYYY } from '../../../utils/dateUtils';

interface PaymentDetails {
  amount: number;
  status: string;
  refNumber: string;
  method: string;
  time: string;
  lender: string;
  paymentDate: string;
}

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);

  useEffect(() => {
    const amount = parseFloat(searchParams.get('amount') || '0');
    const paymentIntentId = searchParams.get('paymentIntentId') || '';
    const timestamp = searchParams.get('timestamp') || new Date().toISOString();
    const method = searchParams.get('method') || 'Stripe';
    const status = searchParams.get('status') || 'succeeded';
    const rawDate = searchParams.get('date');
    const date = rawDate ? formatDateToDDMMYYYY(rawDate) : formatDateToDDMMYYYY(new Date());

    setPaymentDetails({
      amount,
      status: status === 'succeeded' ? 'Completed' : 'Failed',
      refNumber: paymentIntentId.substring(0, 12) || 'N/A',
      method,
      time: new Date(timestamp).toLocaleTimeString('en-GB'),
      lender: 'Quantum Cadence',
      paymentDate: date,
    });
  }, [searchParams]);

  useEffect(() => {
    const loanId = searchParams.get('loanId');
    const status = searchParams.get('status') || 'succeeded';
    if (!loanId || status !== 'succeeded') return;

    const timer = window.setTimeout(() => {
      navigate(`/loans/${loanId}/repayment-history?paymentSuccess=true&t=${Date.now()}`);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [navigate, searchParams]);

  const handleDoneClick = () => {
    const loanId = searchParams.get('loanId');
    navigate(loanId ? `/loans/${loanId}/repayment-history` : '/loans');
  };

  if (!paymentDetails) {
    return null;
  }

  const detailRows: Array<{
    label: string;
    value: string;
    bold?: boolean;
    monospace?: boolean;
  }> = [
    { label: 'Ref Number', value: paymentDetails.refNumber, monospace: true },
    { label: 'Method', value: paymentDetails.method },
    { label: 'Payment Date', value: paymentDetails.paymentDate },
    { label: 'Time', value: paymentDetails.time },
    { label: 'Lender Name', value: paymentDetails.lender, bold: true },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'transparent',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 4, sm: 6 },
        p: { xs: 2, sm: 4 },
        fontFamily: "'Libre Franklin', sans-serif",
        '& .MuiTypography-root, & .MuiButton-root': {
          fontFamily: "'Libre Franklin', sans-serif",
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 640,
          borderRadius: '32px',
          border: '1px solid #d9d9d9',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          textAlign: 'center',
          zIndex: 1,
          overflow: 'hidden',
          background: '#ffffff',
        }}
      >
        <Box
          sx={{
            px: { xs: 3.5, sm: 6 },
            pt: { xs: 5, sm: 7 },
            pb: { xs: 3, sm: 4 },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Typography
            sx={{
              color: '#111111',
              fontWeight: 800,
              fontSize: { xs: 26, sm: 32 },
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Payment Success!
          </Typography>
          <Typography sx={{ color: '#333333' }}>
            Your payment has been successfully completed.
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 0,
            pb: 0,
          }}
        >
          <Box
            sx={{
              borderRadius: '0 0 32px 32px',
              background: '#ffffff',
              borderTop: '1px solid #e5e5e5',
              px: { xs: 3.5, sm: 6 },
              py: { xs: 3.2, sm: 3.6 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2.5, sm: 3 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ color: '#222222' }}>Amount</Typography>
              <Typography sx={{ color: '#4BD184', fontWeight: 700 }}>
                {formatAmount(paymentDetails.amount)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ color: '#222222' }}>Payment Status</Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.75,
                  py: 0.45,
                  borderRadius: 20,
                  fontWeight: 700,
                  fontSize: 14,
                  background: 'rgba(37,139,69,0.95)',
                  color: '#fff',
                  minWidth: 106,
                  justifyContent: 'center',
                }}
              >
                {paymentDetails.status}
              </Box>
            </Box>

            <Box
              sx={{
                height: 1,
                background: '#e5e5e5',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.8, sm: 2 },
              }}
            >
              {detailRows.map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ color: '#222222' }}>{row.label}</Typography>
                  <Typography
                    sx={{
                      color: '#111111',
                      fontWeight: row.bold ? 700 : 500,
                      fontFamily: "'Libre Franklin', sans-serif",
                      letterSpacing: row.monospace ? '0.04em' : 'inherit',
                      textAlign: 'right',
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 360,
        }}
      >
        <PrimaryButton
          onClick={handleDoneClick}
          sx={{
            width: '100%',
            height: 54,
            borderRadius: '999px',
            fontSize: 18,
            fontWeight: 700,
            textTransform: 'none',
          }}
        >
          Done
        </PrimaryButton>
      </Box>
    </Box>
  );
};

export default PaymentSuccess;